import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch, apiDownload, apiOpenBlob } from '../../../lib/api';
import { usePermesso } from '../../../context/PermessiContext';
import { useDirtyForm } from '../../../hooks/useDirtyForm';
import { showToast } from '../../../components/Toast';
import { urlPubblica } from '../../../pubblico/apiPubblica';

type StatoPagina = 'NON_CONFIGURATA' | 'DISATTIVATA' | 'IN_ATTESA_COMMESSA' | 'COMMESSA_CHIUSA' | 'ONLINE';
interface ModelloDisponibile { id: number; nome: string }
// Aspetto della pagina. Il contratto con il backend non dice se questi campi stanno in `data` o in `data.percorso`: si leggono da entrambi.
interface AspettoPercorso {
  modelloId?: number | null;
  colorePrincipale?: string | null;
  coloreSecondario?: string | null;
  haLogoImpresa?: boolean;
  haCopertina?: boolean;
  // Indirizzi pubblici assoluti e già versionati di logo e copertina (null se non ci sono).
  logoImpresaUrl?: string | null;
  copertinaUrl?: string | null;
  modelliDisponibili?: ModelloDisponibile[];
}
interface DatiPercorso extends AspettoPercorso {
  percorso: ({ id: number; tokenPubblico: string; attivo: boolean; scadenzaScelte: string | null; testoBenvenuto: string | null; brochureNomeFile: string | null } & AspettoPercorso) | null;
  statoPagina: StatoPagina;
  linkPubblico: string | null;
  sedeNome: string | null;
}

const ETICHETTA_STATO: Record<StatoPagina, string> = {
  NON_CONFIGURATA: 'Non configurata',
  DISATTIVATA: 'Disattivata',
  IN_ATTESA_COMMESSA: 'Andrà online alla generazione della commessa',
  COMMESSA_CHIUSA: 'Commessa chiusa: prenotazioni chiuse',
  ONLINE: 'Online',
};
const BADGE_STATO: Record<StatoPagina, string> = {
  NON_CONFIGURATA: 'bop-badge-neutral', DISATTIVATA: 'bop-badge-neutral', IN_ATTESA_COMMESSA: 'bop-badge-warning',
  COMMESSA_CHIUSA: 'bop-badge-neutral', ONLINE: 'bop-badge-success',
};

// Colori mostrati dai selettori quando il cantiere usa quelli standard (--bop-color-primary e --bop-color-brand).
const COLORE_PRINCIPALE_STANDARD = '#0369a1';
const COLORE_SECONDARIO_STANDARD = '#0f172a';

const leggiAspetto = (d: DatiPercorso) => ({
  modelloId: d.percorso?.modelloId ?? d.modelloId ?? null,
  colorePrincipale: d.percorso?.colorePrincipale ?? d.colorePrincipale ?? '',
  coloreSecondario: d.percorso?.coloreSecondario ?? d.coloreSecondario ?? '',
  logoImpresaUrl: d.percorso?.logoImpresaUrl ?? d.logoImpresaUrl ?? null,
  copertinaUrl: d.percorso?.copertinaUrl ?? d.copertinaUrl ?? null,
  haLogoImpresa: Boolean(d.percorso?.haLogoImpresa ?? d.haLogoImpresa ?? d.percorso?.logoImpresaUrl ?? d.logoImpresaUrl),
  haCopertina: Boolean(d.percorso?.haCopertina ?? d.haCopertina ?? d.percorso?.copertinaUrl ?? d.copertinaUrl),
});
const leggiModelli = (d: DatiPercorso): ModelloDisponibile[] | null => d.modelliDisponibili ?? d.percorso?.modelliDisponibili ?? null;

type TipoImmagine = 'logo-impresa' | 'copertina';

export default function PercorsoAcquirentiCard({ offertaId, codiceOfferta }: { offertaId: number; codiceOfferta: string }) {
  const navigate = useNavigate();
  const { can } = usePermesso();
  // Modificabile anche dopo la conversione: la pagina va online proprio con la commessa.
  const canEdit = can('commerciale.offerte_capitolato', 'edit');
  const base = `/api/percorso-acquirenti/offerte/${offertaId}`;
  const [dati, setDati] = useState<DatiPercorso | null>(null);
  const [attivo, setAttivo] = useState(false);
  const [scadenza, setScadenza] = useState('');
  const [testo, setTesto] = useState('');
  // Aspetto: '' = pagina standard / colori standard.
  const [modelloId, setModelloId] = useState('');
  const [colorePrincipale, setColorePrincipale] = useState('');
  const [coloreSecondario, setColoreSecondario] = useState('');
  const [modelli, setModelli] = useState<ModelloDisponibile[]>([]);
  // Solo per il caso in cui il backend non mandi gli indirizzi già versionati: cambia a ogni caricamento o rimozione, così l'anteprima non resta quella vecchia in cache.
  const [versioneImmagini, setVersioneImmagini] = useState(0);
  const [salvando, setSalvando] = useState(false);
  const inputFile = useRef<HTMLInputElement>(null);
  const inputLogo = useRef<HTMLInputElement>(null);
  const inputCopertina = useRef<HTMLInputElement>(null);
  // Snapshot vuoto finché i dati non sono caricati; poi aggiornato da applica() (caricamento e salvataggio OK).
  const [initialSnapshot, setInitialSnapshot] = useState('');
  const isDirty = initialSnapshot !== '' && JSON.stringify({ attivo, scadenza, testo, modelloId, colorePrincipale, coloreSecondario }) !== initialSnapshot;
  useDirtyForm(isDirty, `Percorso acquirenti ${codiceOfferta}`);

  const applica = (d: DatiPercorso) => {
    const aspetto = leggiAspetto(d);
    const valori = {
      attivo: d.percorso?.attivo ?? false,
      scadenza: d.percorso?.scadenzaScelte ? d.percorso.scadenzaScelte.slice(0, 10) : '',
      testo: d.percorso?.testoBenvenuto ?? '',
      modelloId: aspetto.modelloId ? String(aspetto.modelloId) : '',
      colorePrincipale: aspetto.colorePrincipale ?? '',
      coloreSecondario: aspetto.coloreSecondario ?? '',
    };
    setDati(d);
    setAttivo(valori.attivo);
    setScadenza(valori.scadenza);
    setTesto(valori.testo);
    setModelloId(valori.modelloId);
    setColorePrincipale(valori.colorePrincipale);
    setColoreSecondario(valori.coloreSecondario);
    const elencoModelli = leggiModelli(d);
    if (elencoModelli) setModelli(elencoModelli);
    setInitialSnapshot(JSON.stringify(valori));
  };

  const leggi = useCallback(() => {
    // @api GET /api/percorso-acquirenti/offerte/:offertaId -> {"success":true,"data":{"percorso":"object","statoPagina":"string","linkPubblico":"string","modelliDisponibili":"array","logoImpresaUrl":"string","copertinaUrl":"string"}}
    return apiFetch<{ success: boolean; data: DatiPercorso }>(base).then(r => r.data);
  }, [base]);

  const carica = useCallback(() => {
    leggi().then(applica).catch(e => showToast(e.message, 'error'));
  }, [leggi]);

  useEffect(() => { carica(); }, [carica]);

  // Dopo il caricamento o la rimozione di un'immagine si aggiornano solo i dati del server (stato, link, logo e copertina):
  // i campi del modulo non ancora salvati restano come sono. La risposta di POST e DELETE è il payload della card;
  // se non lo fosse, lo si rilegge.
  const aggiornaDatiServer = async (risposta?: DatiPercorso) => {
    const d = risposta?.percorso !== undefined ? risposta : await leggi();
    setDati(d);
    const elencoModelli = leggiModelli(d);
    if (elencoModelli) setModelli(elencoModelli);
    setVersioneImmagini(v => v + 1);
  };

  const salva = async () => {
    setSalvando(true);
    try {
      // @api PUT /api/percorso-acquirenti/offerte/:offertaId {"attivo":"boolean","scadenzaScelte":"string","testoBenvenuto":"string","modelloId":"number","colorePrincipale":"string","coloreSecondario":"string"} -> {"success":true,"data":"object"}
      const r = await apiFetch<{ success: boolean; data: DatiPercorso }>(base, {
        method: 'PUT',
        body: JSON.stringify({
          attivo, scadenzaScelte: scadenza || null, testoBenvenuto: testo,
          modelloId: modelloId ? Number(modelloId) : null,
          colorePrincipale: colorePrincipale || null,
          coloreSecondario: coloreSecondario || null,
        }),
      });
      applica(r.data);
      showToast('Percorso acquirenti salvato');
    } catch (e) {
      showToast((e as Error).message, 'error');
    } finally {
      setSalvando(false);
    }
  };

  const caricaBrochure = async (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    try {
      // @api POST /api/percorso-acquirenti/offerte/:offertaId/brochure [FormData file] -> {"success":true,"data":"object"}
      const r = await apiFetch<{ success: boolean; data: DatiPercorso }>(`${base}/brochure`, { method: 'POST', body: fd });
      // Aggiorna solo nome brochure, stato e link: i campi del modulo non ancora salvati restano.
      setDati(r.data);
      showToast('Brochure caricata');
    } catch (e) {
      showToast((e as Error).message, 'error');
    } finally {
      if (inputFile.current) inputFile.current.value = '';
    }
  };

  const caricaImmagine = async (tipo: TipoImmagine, file: File, input: React.RefObject<HTMLInputElement | null>) => {
    const fd = new FormData();
    fd.append('file', file);
    try {
      // @api POST /api/percorso-acquirenti/offerte/:offertaId/logo-impresa [FormData file] -> {"success":true,"data":{"percorso":"object","statoPagina":"string","linkPubblico":"string","logoImpresaUrl":"string","copertinaUrl":"string"}}
      // @api POST /api/percorso-acquirenti/offerte/:offertaId/copertina [FormData file] -> {"success":true,"data":{"percorso":"object","statoPagina":"string","linkPubblico":"string","logoImpresaUrl":"string","copertinaUrl":"string"}}
      const r = await apiFetch<{ success: boolean; data?: DatiPercorso }>(`${base}/${tipo}`, { method: 'POST', body: fd });
      await aggiornaDatiServer(r?.data);
      showToast(tipo === 'logo-impresa' ? "Logo dell'impresa caricato" : 'Copertina caricata');
    } catch (e) {
      showToast((e as Error).message, 'error');
    } finally {
      if (input.current) input.current.value = '';
    }
  };

  const rimuoviImmagine = async (tipo: TipoImmagine) => {
    try {
      // @api DELETE /api/percorso-acquirenti/offerte/:offertaId/logo-impresa -> {"success":true,"data":{"percorso":"object","statoPagina":"string","linkPubblico":"string","logoImpresaUrl":"string","copertinaUrl":"string"}}
      // @api DELETE /api/percorso-acquirenti/offerte/:offertaId/copertina -> {"success":true,"data":{"percorso":"object","statoPagina":"string","linkPubblico":"string","logoImpresaUrl":"string","copertinaUrl":"string"}}
      const r = await apiFetch<{ success: boolean; data?: DatiPercorso }>(`${base}/${tipo}`, { method: 'DELETE' });
      await aggiornaDatiServer(r?.data);
      showToast(tipo === 'logo-impresa' ? "Logo dell'impresa rimosso" : 'Copertina rimossa');
    } catch (e) {
      showToast((e as Error).message, 'error');
    }
  };

  const apriBrochure = () => {
    // @api GET /api/percorso-acquirenti/offerte/:offertaId/brochure -> file PDF (inline)
    apiOpenBlob(`${base}/brochure`).catch(e => showToast(e.message, 'error'));
  };

  const scaricaQr = () => {
    // @api GET /api/percorso-acquirenti/offerte/:offertaId/qr -> file PNG (attachment)
    apiDownload(`${base}/qr`, `qr-${codiceOfferta}.png`).catch(e => showToast(e.message, 'error'));
  };

  const copiaLink = async () => {
    if (!dati?.linkPubblico) return;
    await navigator.clipboard.writeText(dati.linkPubblico);
    showToast('Link copiato');
  };

  if (!dati) return null;

  const { haLogoImpresa, haCopertina, logoImpresaUrl, copertinaUrl } = leggiAspetto(dati);
  const token = dati.percorso?.tokenPubblico;
  // Anteprima di logo e copertina dall'indirizzo pubblico (quello che vedrà l'acquirente), già versionato dal backend.
  // Senza, lo si compone dal token con un contatore al posto della versione.
  const srcImmagine = (tipo: TipoImmagine, urlBackend: string | null): string | null => {
    if (urlBackend) return urlBackend;
    return token ? urlPubblica(`/api/pubblico/prenotazioni/${encodeURIComponent(token)}/${tipo}?v=${versioneImmagini}`) : null;
  };
  const srcLogo = haLogoImpresa ? srcImmagine('logo-impresa', logoImpresaUrl) : null;
  const srcCopertina = haCopertina ? srcImmagine('copertina', copertinaUrl) : null;
  // Un modello già scelto ma non più in elenco (o elenco non ancora arrivato) resta comunque selezionabile come valore corrente.
  const modelloNonInElenco = modelloId !== '' && !modelli.some(m => String(m.id) === modelloId);

  return (
    <div className="bop-card bop-percorso__sezione">
      <div className="ofc-section-header">
        <span className="ofc-section-label">Percorso acquirenti</span>
        <span className={`bop-badge ${BADGE_STATO[dati.statoPagina]}`}>{ETICHETTA_STATO[dati.statoPagina]}</span>
      </div>
      <p className="bop-text-muted">
        Pagina pubblica da dare all'impresa: gli acquirenti prenotano il primo incontro
        {dati.sedeNome ? ` presso ${dati.sedeNome}` : ''} e vengono registrati nella commessa.
      </p>

      <div className="bop-form-row">
        <div className="bop-form-group">
          <label className="bop-label">Pagina attiva</label>
          <button type="button" className={`bop-switch${attivo ? ' bop-switch--on' : ''}`} onClick={() => setAttivo(!attivo)} disabled={!canEdit} aria-label="Pagina attiva" />
        </div>
        <div className="bop-form-group">
          <label className="bop-label">Scadenza delle scelte</label>
          <input className="bop-input" type="date" value={scadenza} onChange={e => setScadenza(e.target.value)} disabled={!canEdit} />
        </div>
      </div>
      <div className="bop-form-group">
        <label className="bop-label">Testo di benvenuto (facoltativo)</label>
        <textarea className="bop-input bop-percorso__textarea" value={testo} onChange={e => setTesto(e.target.value)} disabled={!canEdit} />
      </div>

      <div className="bop-percorso__sezione">
        <h3 className="bop-card__title">Aspetto della pagina</h3>
        <p className="bop-text-muted">
          Modello e colori si salvano con «Salva percorso»; logo e copertina si caricano subito.
        </p>
        <div className="bop-percorso__griglia">
          <div className="bop-form-group">
            <label className="bop-label" htmlFor="pa-modello">Modello</label>
            <select id="pa-modello" className="bop-select" value={modelloId} onChange={e => setModelloId(e.target.value)} disabled={!canEdit}>
              <option value="">Pagina standard</option>
              {modelloNonInElenco && <option value={modelloId}>Modello {modelloId}</option>}
              {modelli.map(m => <option key={m.id} value={String(m.id)}>{m.nome}</option>)}
            </select>
          </div>
          <div className="bop-form-group">
            <label className="bop-label" htmlFor="pa-colore-principale">Colore principale</label>
            <div className="bop-percorso__colore">
              <input id="pa-colore-principale" className="bop-percorso__colore-input" type="color" value={colorePrincipale || COLORE_PRINCIPALE_STANDARD} onChange={e => setColorePrincipale(e.target.value)} disabled={!canEdit} />
              {colorePrincipale
                ? canEdit && <button type="button" className="bop-btn bop-btn-secondary bop-btn-sm" onClick={() => setColorePrincipale('')} aria-label="Ripristina il colore principale">Ripristina</button>
                : <span className="bop-text-muted">Standard</span>}
            </div>
          </div>
          <div className="bop-form-group">
            <label className="bop-label" htmlFor="pa-colore-secondario">Colore secondario</label>
            <div className="bop-percorso__colore">
              <input id="pa-colore-secondario" className="bop-percorso__colore-input" type="color" value={coloreSecondario || COLORE_SECONDARIO_STANDARD} onChange={e => setColoreSecondario(e.target.value)} disabled={!canEdit} />
              {coloreSecondario
                ? canEdit && <button type="button" className="bop-btn bop-btn-secondary bop-btn-sm" onClick={() => setColoreSecondario('')} aria-label="Ripristina il colore secondario">Ripristina</button>
                : <span className="bop-text-muted">Standard</span>}
            </div>
          </div>
        </div>

        <div className="bop-percorso__riga">
          <span className="bop-label">Logo dell'impresa:</span>
          {srcLogo ? <img className="bop-percorso__anteprima-img" src={srcLogo} alt="Logo dell'impresa" /> : <span className="bop-text-muted">nessuno</span>}
          {canEdit && (
            <>
              <input ref={inputLogo} type="file" accept="image/jpeg,image/png,image/webp,image/svg+xml" className="bop-percorso__file" aria-label="File del logo dell'impresa" onChange={e => e.target.files?.[0] && caricaImmagine('logo-impresa', e.target.files[0], inputLogo)} />
              <button type="button" className="bop-btn bop-btn-secondary bop-btn-sm" onClick={() => inputLogo.current?.click()}>{haLogoImpresa ? 'Sostituisci logo' : 'Carica logo'}</button>
              {haLogoImpresa && <button type="button" className="bop-btn bop-btn-secondary bop-btn-sm" onClick={() => rimuoviImmagine('logo-impresa')}>Rimuovi logo</button>}
            </>
          )}
        </div>
        <div className="bop-percorso__riga">
          <span className="bop-label">Copertina:</span>
          {srcCopertina ? <img className="bop-percorso__anteprima-img bop-percorso__anteprima-img--copertina" src={srcCopertina} alt="Copertina del cantiere" /> : <span className="bop-text-muted">nessuna</span>}
          {canEdit && (
            <>
              <input ref={inputCopertina} type="file" accept="image/jpeg,image/png,image/webp,image/svg+xml" className="bop-percorso__file" aria-label="File della copertina" onChange={e => e.target.files?.[0] && caricaImmagine('copertina', e.target.files[0], inputCopertina)} />
              <button type="button" className="bop-btn bop-btn-secondary bop-btn-sm" onClick={() => inputCopertina.current?.click()}>{haCopertina ? 'Sostituisci copertina' : 'Carica copertina'}</button>
              {haCopertina && <button type="button" className="bop-btn bop-btn-secondary bop-btn-sm" onClick={() => rimuoviImmagine('copertina')}>Rimuovi copertina</button>}
            </>
          )}
        </div>
        <div className="bop-percorso__riga">
          <button type="button" className="bop-btn bop-btn-secondary bop-btn-sm" onClick={() => navigate(`/commerciale/percorso/offerte/${offertaId}/anteprima`)} disabled={!dati.percorso} title={dati.percorso ? undefined : 'Salva prima il percorso'}>Anteprima</button>
          {isDirty && <span className="bop-text-muted">L'anteprima mostra l'ultima versione salvata.</span>}
        </div>
      </div>

      {canEdit && (
        <div className="bop-percorso__riga">
          <button className="bop-btn bop-btn-primary" onClick={salva} disabled={salvando}>{salvando ? 'Salvataggio…' : 'Salva percorso'}</button>
        </div>
      )}

      <div className="bop-percorso__riga">
        <span className="bop-label">Brochure del capitolato:</span>
        {dati.percorso?.brochureNomeFile ? (
          <button className="bop-btn bop-btn-secondary bop-btn-sm" onClick={apriBrochure}>
            {dati.percorso.brochureNomeFile}
          </button>
        ) : (
          <span className="bop-text-muted">nessuna</span>
        )}
        {canEdit && (
          <>
            <input ref={inputFile} type="file" accept="application/pdf" className="bop-percorso__file" onChange={e => e.target.files?.[0] && caricaBrochure(e.target.files[0])} />
            <button className="bop-btn bop-btn-secondary bop-btn-sm" onClick={() => inputFile.current?.click()}>Carica PDF</button>
          </>
        )}
      </div>

      {dati.linkPubblico && (
        <div className="bop-percorso__riga">
          <span className="bop-percorso__link">{dati.linkPubblico}</span>
          <button className="bop-btn bop-btn-secondary bop-btn-sm" onClick={copiaLink}>Copia link</button>
          <button className="bop-btn bop-btn-secondary bop-btn-sm" onClick={scaricaQr}>Scarica QR</button>
        </div>
      )}
    </div>
  );
}
